import { LinkCardTabs } from 'components/Card/CardTabs/LinkCardTabs';
import { Card } from 'components/common';
import { CommonButton } from 'components/common/Forms/Button';
import { Col, Grid } from 'components/common/Grid';
import { PageHelmet } from 'components/common/PageHelmet';
import { StaffSettingsTimePlanningTree } from 'configs/RoutesConfig';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import CheckboxInput from '../../../../components/common/Forms/CheckboxInput';
import { ControlledDatePickerInput } from '../../../../components/common/Forms/controlled/ControlledDatePickerInput';
import {
  FormActionsStyle,
  FormBody,
  FormHeader,
  FormRow,
  FormTitle,
} from '../../../../components/common/Forms/Forms.styled';
import CommonInput from '../../../../components/common/Forms/Input';
import { CardLoader } from '../../../../components/common/Loading';
import { SUPERADMIN } from '../../../../configs/constants';
import {
  SITE_WEEK_DAYS,
  STAFF_CLOSE_HOUR_IN_SECONDS,
  STAFF_OPEN_HOUR_IN_SECONDS,
} from '../../../../configs/siteAndTrustAttributes';
import { useRole } from '../../../../context/RoleContext';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useMaxAndMinFutureDateForStaffBooking } from '../../../../hooks/patient/booking/useMaxAndMinFutureDateForBooking';
import {
  useSiteIntAttributes,
  useSiteNumericArrayAttribute,
} from '../../../../hooks/useAttribute';
import {
  formatSecondsOfDayIntoTime,
  getServerTime,
  isAfterOrSameDay,
  isBeforeOrSameDay,
  startOfDay,
} from '../../../../utils/dateUtil';
import { StaffLayout } from '../../StaffLayout';
import {
  MassCancelFormValues,
  useMassCancelValidation,
} from './StaffMassCancelHooks';
import { StaffMassCancelConfirmDialog } from './StaffMassCancelConfirmDialog';

export default function StaffMassCancel() {
  const { t } = useCommonTranslation();
  const [formValuesToConfirm, setFormValuesToConfirm] =
    useState<MassCancelFormValues>();

  const { hasAnyOfRoles } = useRole();
  const canCancelMoreDays = hasAnyOfRoles(SUPERADMIN);

  const [openHourInSeconds, closeHourInSeconds] = useSiteIntAttributes(
    STAFF_OPEN_HOUR_IN_SECONDS,
    STAFF_CLOSE_HOUR_IN_SECONDS,
  );

  const {
    isLoading: isOpeningHoursLoading,
    openTime,
    closeTime,
  } = useMemo(() => {
    if (
      typeof openHourInSeconds !== 'number' ||
      typeof closeHourInSeconds !== 'number'
    ) {
      return { isLoading: true };
    }
    return {
      isLoading: false,
      openTime: formatSecondsOfDayIntoTime(openHourInSeconds),
      closeTime: formatSecondsOfDayIntoTime(closeHourInSeconds),
    };
  }, [openHourInSeconds, closeHourInSeconds]);

  const isLoading = isOpeningHoursLoading;

  const validateResolver = useMassCancelValidation();
  const {
    register,
    control,
    formState,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<MassCancelFormValues>({
    resolver: validateResolver,
    defaultValues: {
      fullDay: true,
      fromTime: openTime,
      toTime: closeTime,
      clearApptTemplate: true,
    },
  });
  const values = watch();
  const { fullDay } = values;

  const openMassCancelConfirmDialog = () => {
    handleSubmit(() => {
      setFormValuesToConfirm(values);
    })();
  };

  const { maxDate: maxSite } = useMaxAndMinFutureDateForStaffBooking();
  const weekDays = useSiteNumericArrayAttribute(SITE_WEEK_DAYS);

  const isDayAvailable = useCallback(
    (date: Date) => {
      return (
        isAfterOrSameDay(startOfDay(date), startOfDay(getServerTime())) &&
        isBeforeOrSameDay(startOfDay(date), startOfDay(maxSite)) &&
        weekDays?.includes(date.getDay())
      );
    },
    [maxSite, weekDays],
  );

  const fullDayCheckbox = (
    <CheckboxInput
      onlyInRow
      label={t('mass-cancel-full-day')}
      key="fullDay"
      id="fullDay"
      isToggle={false}
      {...register('fullDay')}
      checked={fullDay}
    />
  );

  return (
    <StaffLayout>
      <PageHelmet title={t('mass-cancel')} />
      <Grid>
        <Col md={5}>
          <Card>
            {isLoading ? (
              <CardLoader />
            ) : (
              <>
                <LinkCardTabs tabs={StaffSettingsTimePlanningTree(t)} />
                <FormHeader>
                  <FormTitle>{t('mass-cancel')}</FormTitle>
                </FormHeader>
                <FormBody>
                  <FormRow>
                    <ControlledDatePickerInput
                      control={control}
                      formState={formState}
                      helperText={errors.date?.message}
                      hasError={Boolean(errors.date)}
                      name="date"
                      label={canCancelMoreDays ? t('date-from') : t('date')}
                      placeholder={t('pick-date')}
                      filterDate={isDayAvailable}
                    />
                    {canCancelMoreDays ? (
                      <ControlledDatePickerInput
                        control={control}
                        formState={formState}
                        helperText={errors.toDate?.message}
                        hasError={Boolean(errors.toDate)}
                        name="toDate"
                        label={t('date-to')}
                        placeholder={t('mass-cancel-same-day')}
                        filterDate={isDayAvailable}
                      />
                    ) : (
                      fullDayCheckbox
                    )}
                  </FormRow>
                  {canCancelMoreDays && <FormRow>{fullDayCheckbox}</FormRow>}
                  {!fullDay && (
                    <FormRow>
                      <CommonInput
                        {...register('fromTime')}
                        placeholder={t('hour-from')}
                        label={t('hour-from')}
                        hasError={Boolean(errors.fromTime)}
                        helperText={errors?.fromTime?.message}
                        disabled={fullDay}
                      />
                      <CommonInput
                        {...register('toTime')}
                        placeholder={t('hour-to')}
                        label={t('hour-to')}
                        hasError={Boolean(errors.toTime)}
                        helperText={errors?.toTime?.message}
                        disabled={fullDay}
                      />
                    </FormRow>
                  )}
                  <FormRow>
                    <CheckboxInput
                      label={t('mass-cancel-clear-appt-template')}
                      checked={values.clearApptTemplate}
                      {...register('clearApptTemplate')}
                    />
                  </FormRow>
                  <FormActionsStyle align="center" topMargin={true}>
                    <CommonButton
                      variant="danger"
                      type="button"
                      onClick={() => openMassCancelConfirmDialog()}
                    >
                      {t('submit-mass-cancel')}
                    </CommonButton>
                  </FormActionsStyle>
                </FormBody>
              </>
            )}
          </Card>
        </Col>
      </Grid>
      {formValuesToConfirm && (
        <StaffMassCancelConfirmDialog
          formValues={formValuesToConfirm}
          onClose={() => setFormValuesToConfirm(undefined)}
        />
      )}
    </StaffLayout>
  );
}
